import React, { useState } from 'react';
import { useCurrency } from '../contexts/CurrencyContext';
import { 
  Button, 
  Snackbar, 
  Alert, 
  CircularProgress,
  Box 
} from '@mui/material';
import { Share, Check, ContentCopy } from '@mui/icons-material';
import { encodeMeetingData, generateShareUrl, copyToClipboard } from '../utils/shareUtils';

export default function ShareButton({ 
  meetingName, 
  attendees, 
  elapsedSeconds, 
  finalCost,
  recurringData = null,
  sourceCalculator = 'live',
  variant = 'outlined',
  size = 'large',
  sx = {}
}) {
  const [isSharing, setIsSharing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const { currency, region } = useCurrency();

  const handleShare = async () => {
    setIsSharing(true);
    
    try {
      // Encode the meeting data
      const encodedData = encodeMeetingData(meetingName, attendees, elapsedSeconds, finalCost, recurringData, sourceCalculator, currency, region);
      
      if (!encodedData) {
        throw new Error('Failed to encode meeting data');
      }
      
      // Generate the share URL
      const shareUrl = generateShareUrl(encodedData);
      
      // Copy to clipboard
      const success = await copyToClipboard(shareUrl);
      
      if (success) {
        setShowSuccess(true);
      } else {
        throw new Error('Failed to copy to clipboard');
      }
    } catch (error) {
      console.error('Error sharing meeting:', error);
      setShowError(true);
    } finally {
      setIsSharing(false);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  return (
    <Box>
      <Button
        variant={variant}
        size={size}
        onClick={handleShare}
        disabled={isSharing}
        aria-label={
          isSharing 
            ? 'Generating share link, please wait' 
            : showSuccess 
              ? 'Share link successfully copied to clipboard' 
              : 'Generate and copy share link to clipboard'
        }
        aria-describedby={showSuccess ? 'share-success' : showError ? 'share-error' : undefined}
        startIcon={
          isSharing ? (
            <CircularProgress size={20} color="inherit" />
          ) : showSuccess ? (
            <Check />
          ) : (
            <Share />
          )
        }
        sx={{
          fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
          fontWeight: 700,
          borderRadius: 3,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
          ...sx,
        }}
      >
        {isSharing ? 'Generating Link...' : showSuccess ? 'Link Copied!' : 'Share Results'}
      </Button>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          id="share-success"
          onClose={handleCloseSuccess} 
          severity="success" 
          variant="filled"
          icon={<ContentCopy />}
          role="status"
          aria-live="polite"
          sx={{ 
            fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
            fontWeight: 600,
          }}
        >
          Share link copied to clipboard! Send it to anyone to view this meeting summary.
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          id="share-error"
          onClose={handleCloseError} 
          severity="error" 
          variant="filled"
          role="alert"
          aria-live="assertive"
          sx={{ 
            fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
            fontWeight: 600,
          }}
        >
          Failed to create share link. Please try again.
        </Alert>
      </Snackbar>
    </Box>
  );
}